import * as React from "react";
import classNames from "classnames";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { connect } from "react-redux";

import Typography from "@material-ui/core/Typography";
import { Reservation } from "../../api/reservations";
import { Table } from "../../api/tables";
import { StoreState } from "../../reducers/root-reducer";
import { ConnectedListSection } from "../inquiry-list/list-section";
import {
	getReservationsForTable,
	getSelectedOccupiedDay,
	getTotalSoftLimitForTable,
	getPeopleCountSumForTable,
	getTimeFilteredReservations
} from "../../util/general-utility";
import { format, formatDate } from "../../util/date-utility";
import { ConnectedPlannerListReservation } from "./reservation";
import { OccupiedDay } from "../../api/occupied-days";
import { getGroupDataForTable } from "../../util/table-link-utility";
import { setMovingReservation } from "../../actions/store/planner-list-actions";
import { Dispatch } from "../../../node_modules/redux";
import {
	removeTableFromLinkGroup,
	addTableToLinkGroup
} from "../../actions/store/app-actions";
import { updateSingleReservation } from "../../actions/reservation-actions";
import { Area } from "../../api/areas";

interface State {}

export interface PlannerListTableProps extends Table {
	readOnly?: Boolean;
}

interface ConnectedPlannerListTableProps extends WithStyles<typeof styles> {
	areas: Area[];
	tables: Table[];
	reservations: Reservation[];
	selectedDate: Date;
	selectedTime: string;
	occupiedDays: OccupiedDay[];
	tableLinkModeActive: Boolean;
	tableLinkDeletionModeActive: Boolean;
	tempTableLinkGroups: string[][];
	movingReservationId: string;
	setMovingReservation: (id: string) => void;
	addTableToLinkGroup: (id: string) => void;
	removeTableFromLinkGroup: (id: string) => void;
}

type Props = PlannerListTableProps & ConnectedPlannerListTableProps;

export class PlannerListTable extends React.Component<Props, {}> {
	private getGroupData(id: string) {
		const {
			occupiedDays,
			selectedDate,
			selectedTime,
			tableLinkModeActive,
			tableLinkDeletionModeActive,
			tempTableLinkGroups
		} = this.props;
		let rawGroupData: string[][] = [[]];

		if (tableLinkModeActive || tableLinkDeletionModeActive) {
			rawGroupData = tempTableLinkGroups;
		} else {
			const occupiedDay = getSelectedOccupiedDay(
				occupiedDays,
				selectedDate
			);

			if (occupiedDay) {
				rawGroupData = occupiedDay.linkedTables;
			}
		}

		return {
			groupData: getGroupDataForTable(id, rawGroupData),
			groups: rawGroupData
		};
	}

	private handleTitleClick = () => {
		const {
			id,
			reservations,
			setMovingReservation,
			movingReservationId,
			tableLinkModeActive,
			tableLinkDeletionModeActive,
			addTableToLinkGroup,
			removeTableFromLinkGroup,
			readOnly
		} = this.props;

		if (readOnly) return;

		if (tableLinkModeActive) {
			addTableToLinkGroup(id);
		} else if (tableLinkDeletionModeActive) {
			removeTableFromLinkGroup(id);
		} else {
			const movingReservation = reservations.find(
				res => res.id === movingReservationId
			);

			if (movingReservation) {
				updateSingleReservation(movingReservation.id, {
					...movingReservation,
					tableId: id
				});
			}

			setMovingReservation(null);
		}
	};

	private renderTableContent = () => {
		const {
			reservations,
			selectedDate,
			selectedTime,
			classes,
			id,
			readOnly
		} = this.props;

		const tableReservations = getReservationsForTable(
			id,
			reservations,
			selectedDate
		);

		if (tableReservations.length === 0) {
			return (
				<div className={classes.noReservationAvailable}>
					<Typography className={classes.noReservationAvailableText}>
						Keine Reservierungen vorhanden!
					</Typography>
				</div>
			);
		}

		// Filter By Time
		let filteredReservations: Array<Reservation> = getTimeFilteredReservations(tableReservations, selectedTime);

		if (filteredReservations.length === 0) {
			return (
				<div className={classes.noReservationAvailable}>
					<Typography className={classes.noReservationAvailableText}>
						Keine Reservierungen vorhanden! (Filter)
					</Typography>
				</div>
			);
		}

		return filteredReservations.map(res => (
			<ConnectedPlannerListReservation
				key={res.id}
				readOnly={readOnly}
				{...res}
			/>
		));
	};

	private renderSectionOverlay = () => {
		const { classes, movingReservationId, readOnly } = this.props;

		if (readOnly) return null;

		return (
			<div
				className={classNames(classes.sectionOverlay, {
					[classes.sectionOverlayVisible]: Boolean(
						movingReservationId
					)
				})}
			>
				<Typography className={classes.sectionOverlayText}>
					Zum Verschieben hier tippen
				</Typography>
				<svg
					className={classes.sectionOverlayBorder}
					viewBox="0 0 100 100"
					preserveAspectRatio="none"
				>
					<rect
						x="0"
						y="0"
						width="100"
						height="100"
						className={classes.sectionOverlayPath}
						vector-effect="non-scaling-stroke"
					/>
				</svg>
			</div>
		);
	};

	public render() {
		const { id, displayName, tables, reservations, areaId, areas, selectedTime } = this.props;

		const groups = this.getGroupData(id);
		const groupData = groups.groupData;
		const softLimit = getTotalSoftLimitForTable(id, tables, groups.groups);

		// Filter By Time
		let filteredReservations: Array<Reservation> = getTimeFilteredReservations(reservations, selectedTime);

		const peopleCount = getPeopleCountSumForTable(
			id,
			filteredReservations,
			groups.groups
		);

		const areaName = areas.reduce((acc, area) => {
			if (area.id === areaId) {
				acc = area.name;
			}

			return acc;
		}, "")

		return (
			<ConnectedListSection
				key={id}
				title={`${displayName} - ${peopleCount}/${softLimit} - ${areaName}`}
				titleBackground={groupData.color}
				onTitleClick={this.handleTitleClick}
				overlay={this.renderSectionOverlay()}
			>
				{this.renderTableContent()}
			</ConnectedListSection>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	areas: state.areas,
	tables: state.tables,
	reservations: state.reservations,
	selectedDate: state.date,
	occupiedDays: state.occupiedDays,
	selectedTime: state.overviewActiveTimeId,
	tableLinkModeActive: state.tableLinkModeActive,
	tableLinkDeletionModeActive: state.tableLinkDeletionModeActive,
	tempTableLinkGroups: state.tempTableLinkGroups,
	movingReservationId: state.plannerListMovingReservation
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	setMovingReservation: (id: string) => dispatch(setMovingReservation(id)),
	addTableToLinkGroup: (id: string) => dispatch(addTableToLinkGroup(id)),
	removeTableFromLinkGroup: (id: string) =>
		dispatch(removeTableFromLinkGroup(id))
});

const styles = (theme: Theme) =>
	createStyles({
		noReservationAvailable: {
			padding: "20px 0"
		},
		noReservationAvailableText: {
			fontStyle: "italic",
			color: "#999",

			"@media (max-width: 959px)": {
				paddingLeft: 10
			}
		},
		sectionOverlay: {
			position: "absolute",
			top: "0",
			left: "0",
			width: "100%",
			height: "100%",
			background: "rgba(0, 0, 0, 0.3)",
			padding: 10,
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			opacity: 0,
			pointerEvents: "none",
			transition: "opacity 0.2s ease"
		},
		sectionOverlayVisible: {
			opacity: 1
		},
		sectionOverlayBorder: {
			position: "absolute",
			top: 10,
			left: 10,
			right: 10,
			bottom: 10,
			width: "calc(100% - 20px)",
			height: "calc(100% - 20px)"
		},
		sectionOverlayPath: {
			fill: "none",
			stroke: "white",
			strokeWidth: 3,
			strokeDasharray: "10, 10"
		},
		sectionOverlayText: {
			color: "white",
			fontWeight: 600
		}
	});

export const ConnectedPlannerListTable = withStyles(styles)(
	connect(
		mapStateToProps,
		mapDispatchToProps
	)(PlannerListTable)
);
